/**
 * Types de prédateur du livre de base V5, plus le Pilleur de tombe (supplément, saisi de mémoire).
 * Chaque type donne : une réserve de chasse, une spécialité au choix, +1 point dans une discipline au choix,
 * des avantages / handicaps et parfois un changement d'Humanité ou de Puissance du Sang.
 * `clans` sur une discipline : réservée à ces clans (ex : Magie du sang).
 * `advantageChoice` : répartition laissée au joueur, décrite en texte.
 */

const specialty = (skill, fr, en) => ({skill, name: {fr, en}});
// kind : 'advantage' ou 'flaw' (cf. ADVANTAGE_KIND dans character.js)
const advantage = (kind, fr, en, level) => ({kind, name: {fr, en}, level});

export const PREDATOR_TYPES = [
  {
    id: 'alleycat',
    name: {fr: 'Chat de gouttière', en: 'Alleycat'},
    huntPool: [['strength', 'brawl']],
    specialties: [specialty('intimidation', 'Braquages', 'Stickups'), specialty('brawl', 'Saisies', 'Grappling')],
    disciplines: [{id: 'celerity'}, {id: 'potence'}],
    humanity: -1,
    advantages: [advantage('advantage', 'Contacts criminels', 'Criminal contacts', 3)],
  },
  {
    id: 'bagger',
    name: {fr: 'Récupérateur', en: 'Bagger'},
    huntPool: [['intelligence', 'streetwise']],
    specialties: [specialty('larceny', 'Crochetage', 'Lock picking'), specialty('streetwise', 'Marché noir', 'Black market')],
    disciplines: [{id: 'bloodSorcery', clans: ['tremere', 'banuHaqim']}, {id: 'obfuscate'}],
    advantages: [
      advantage('advantage', 'Estomac de fer', 'Iron Gullet', 3),
      advantage('flaw', 'Ennemi', 'Enemy', 2),
    ],
  },
  {
    id: 'bloodLeech',
    name: {fr: 'Sangsue', en: 'Blood Leech'},
    huntPool: [],
    specialties: [specialty('brawl', 'Caïnites', 'Kindred'), specialty('stealth', 'Contre les Caïnites', 'Against Kindred')],
    disciplines: [{id: 'celerity'}, {id: 'protean'}],
    humanity: -1,
    bloodPotency: 1,
    advantages: [advantage('flaw', 'Exclusion de proie : mortels', 'Prey Exclusion: mortals', 2)],
    advantageChoice: {
      fr: 'Choisir un handicap à 2 : Sombre secret (diablerie) ou Rejeté.',
      en: 'Pick a 2-dot flaw: Dark Secret (diablerist) or Shunned.',
    },
  },
  {
    id: 'cleaver',
    name: {fr: 'Fendeur', en: 'Cleaver'},
    huntPool: [['manipulation', 'subterfuge']],
    specialties: [specialty('persuasion', 'Manipulation mentale', 'Gaslighting'), specialty('subterfuge', 'Dissimulation', 'Coverups')],
    disciplines: [{id: 'dominate'}, {id: 'animalism'}],
    advantages: [
      advantage('flaw', 'Sombre secret : Fendeur', 'Dark Secret: Cleaver', 1),
      advantage('advantage', 'Troupeau', 'Herd', 2),
    ],
  },
  {
    id: 'consensualist',
    name: {fr: 'Consensuel', en: 'Consensualist'},
    huntPool: [['manipulation', 'persuasion']],
    specialties: [specialty('medicine', 'Phlébotomie', 'Phlebotomy'), specialty('persuasion', 'Victimes', 'Vessels')],
    disciplines: [{id: 'auspex'}, {id: 'fortitude'}],
    humanity: 1,
    advantages: [
      advantage('flaw', 'Sombre secret : briseur de Mascarade', 'Dark Secret: Masquerade Breacher', 1),
      advantage('flaw', 'Exclusion de proie : non-consentants', 'Prey Exclusion: non-consenting', 1),
    ],
  },
  {
    id: 'farmer',
    name: {fr: 'Fermier', en: 'Farmer'},
    huntPool: [['composure', 'animalKen']],
    specialties: [specialty('animalKen', 'Un animal précis', 'Specific animal'), specialty('survival', 'Chasse', 'Hunting')],
    disciplines: [{id: 'animalism'}, {id: 'protean'}],
    humanity: 1,
    excludedClans: ['ventrue'],
    advantages: [advantage('flaw', 'Végétarien (sang animal)', 'Farmer (animal blood)', 2)],
  },
  {
    id: 'graveRobber',
    name: {fr: 'Pilleur de tombe', en: 'Grave Robber'},
    huntPool: [['resolve', 'medicine']],
    specialties: [specialty('occult', 'Objets funéraires', 'Grave goods'), specialty('medicine', 'Cadavres', 'Cadavers')],
    disciplines: [{id: 'fortitude'}, {id: 'oblivion'}],
    advantages: [
      advantage('advantage', 'Estomac de fer', 'Iron Gullet', 3),
      advantage('advantage', 'Refuge', 'Haven', 1),
      advantage('flaw', 'Prédateur indéniable', 'Obvious Predator', 2),
    ],
  },
  {
    id: 'osiris',
    name: {fr: 'Osiris', en: 'Osiris'},
    huntPool: [['manipulation', 'subterfuge'], ['intimidation', 'fame']],
    specialties: [specialty('occult', 'Une tradition précise', 'Specific tradition'), specialty('performance', 'Un art précis', 'Specific entertainment')],
    disciplines: [{id: 'bloodSorcery', clans: ['tremere', 'banuHaqim']}, {id: 'presence'}],
    advantages: [],
    advantageChoice: {
      fr: '3 points à répartir entre Gloire et Troupeau ; 2 points de handicaps entre Ennemis et Mythique.',
      en: '3 dots between Fame and Herd; 2 dots of flaws between Enemies and Mythic.',
    },
  },
  {
    id: 'sandman',
    name: {fr: 'Marchand de sable', en: 'Sandman'},
    huntPool: [['dexterity', 'stealth']],
    specialties: [specialty('medicine', 'Anesthésiques', 'Anesthetics'), specialty('stealth', 'Effraction', 'Break-in')],
    disciplines: [{id: 'auspex'}, {id: 'obfuscate'}],
    advantages: [advantage('advantage', 'Ressources', 'Resources', 1)],
  },
  {
    id: 'sceneQueen',
    name: {fr: 'Reine de la scène', en: 'Scene Queen'},
    huntPool: [['manipulation', 'persuasion']],
    specialties: [
      specialty('etiquette', 'Une scène précise', 'Specific scene'),
      specialty('leadership', 'Une scène précise', 'Specific scene'),
      specialty('streetwise', 'Une scène précise', 'Specific scene'),
    ],
    disciplines: [{id: 'dominate'}, {id: 'potence'}],
    advantages: [
      advantage('advantage', 'Gloire', 'Fame', 1),
      advantage('advantage', 'Contact', 'Contact', 1),
    ],
    advantageChoice: {
      fr: 'Choisir un handicap à 1 : Influence (mal vu) ou Exclusion de proie (une autre sous-culture).',
      en: 'Pick a 1-dot flaw: Influence (Disliked) or Prey Exclusion (another subculture).',
    },
  },
  {
    id: 'siren',
    name: {fr: 'Sirène', en: 'Siren'},
    huntPool: [['charisma', 'subterfuge']],
    specialties: [specialty('persuasion', 'Séduction', 'Seduction'), specialty('subterfuge', 'Séduction', 'Seduction')],
    disciplines: [{id: 'fortitude'}, {id: 'presence'}],
    advantages: [
      advantage('advantage', 'Apparence : beau', 'Looks: Beautiful', 2),
      advantage('flaw', 'Ennemi', 'Enemy', 1),
    ],
  },
];

export const PREDATOR_IDS = PREDATOR_TYPES.map(predator => predator.id);
export const PREDATORS_BY_ID = PREDATOR_TYPES.reduce((acc, predator) => ({...acc, [predator.id]: predator}), {});

/** Disciplines que ce type de prédateur peut offrir à un personnage de ce clan. */
export const getPredatorDisciplines = (predatorId, clanId) => (PREDATORS_BY_ID[predatorId]?.disciplines || [])
  .filter(option => !option.clans || option.clans.includes(clanId))
  .map(option => option.id);

export const isPredatorAllowedForClan = (predatorId, clanId) => !(PREDATORS_BY_ID[predatorId]?.excludedClans || []).includes(clanId);
